// frontend/src/contexts/AuthContext.tsx
import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from "react";
import * as api from "../services/api";

export interface UserMembership {
  id: string;
  userId: string;
  organizationId: string;
  role: "OWNER" | "ADMIN" | "MEMBER";
}

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: "OWNER" | "ADMIN" | "MEMBER" | "MANAGER" | "ARTIST";
  organizationId: string;
  memberships?: UserMembership[];
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
}

interface AuthContextType {
  user: User | null;
  organization: Organization | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (data: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    orgName: string;
  }) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const TOKEN_KEY = import.meta.env.VITE_AUTH_TOKEN_KEY || "quelyo_auth_token";

  const [user, setUser] = useState<User | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [token, setToken] = useState<string | null>(
    localStorage.getItem(TOKEN_KEY)
  );
  const [isLoading, setIsLoading] = useState(true);

  // Charger l'utilisateur au démarrage si token existe
  useEffect(() => {
    const loadUser = async () => {
      if (token) {
        try {
          const userData = await api.getCurrentUser(token);
          setUser(userData);
          api.setAuthToken(token);

          // Try to fetch organization if not present in login response
          if (userData.organizationId) {
            // In a real app, we might have an api.getOrganization(id)
          }
        } catch (error) {
          console.error("Token invalid, logging out");
          logout();
        }
      } else {
      }
      setIsLoading(false);
    };

    loadUser();
  }, [token]);

  const login = async (email: string, password: string) => {
    try {
      const result = await api.login(email, password);
      setToken(result.token);
      setUser(result.user);
      setOrganization(result.organization);
      localStorage.setItem(TOKEN_KEY, result.token);
    } catch (error) {
      throw error;
    }
  };

  const register = async (data: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    orgName: string;
  }) => {
    try {
      const result = await api.register(data);
      setToken(result.token);
      setUser(result.user);
      setOrganization(result.organization);
      localStorage.setItem(TOKEN_KEY, result.token);
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setOrganization(null);
    localStorage.removeItem(TOKEN_KEY);
    api.setAuthToken(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        organization,
        token,
        login,
        register,
        logout,
        isAuthenticated: !!user,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth doit être utilisé dans AuthProvider");
  }
  return context;
};
